import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Layout,
  Component,
  MousePointer,
  Settings,
  FileText,
  Sparkles
} from 'lucide-react';
import Navbar4 from '../components/Navbar4';
import { getFirstPromptResults, submitClarificationResponses } from '../services/LLMCalls';
import { getCurrentUserId } from '../services/database';


const CategoryIcon = ({ category }) => {
  const icons = {
    layout: Layout,
    uiComponents: Component,
    interactivity: MousePointer,
    content: FileText,
    additional: Settings
  };

  const Icon = icons[category];
  return <Icon className='w-5 h-5 text-gray-600' />;
};

const QuestionCard = ({ category, question, value, onChange }) => (
  <div className='bg-white rounded-lg shadow-md p-6 flex flex-col gap-4'>
    <div className='flex items-center gap-2'>
      <CategoryIcon category={category} />
      <span className='text-sm font-medium text-gray-600 uppercase'>
        {category.replace(/([A-Z])/g, ' $1').trim()}
      </span>
    </div>

    <p className='text-gray-800 font-medium'>{question}</p>

    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className='w-full p-3 border rounded-md min-h-[100px]'
      placeholder='Enter your response...'
    />
  </div>
);

const Clarifications = () => {
  
  const location = useLocation();
  const navigate = useNavigate();
  const { projectId, promptId } = location.state || {};

  const [questions, setQuestions] = useState([]);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        await getCurrentUserId();
        const result = await getFirstPromptResults(promptId);

        console.log('API Response:', result);

        // Correctly access the nested data
        if (!result?.data?.step1_results?.clarificationQuestions) {
          console.error('Invalid response structure:', result);
          throw new Error('Unable to load questions. Please try again.');
        }

        const clarificationQuestions = result.data.step1_results.clarificationQuestions;
        const allQuestions = [];
        const categories = ['layout', 'uiComponents', 'interactivity', 'content', 'additional'];

        categories.forEach(category => {
          const categoryQuestions = clarificationQuestions[category];
          if (categoryQuestions?.questionsFound && Array.isArray(categoryQuestions.questions)) {
            categoryQuestions.questions.forEach(question => {
              allQuestions.push({ category, question });
            });
          }
        });

        if (allQuestions.length === 0) {
          throw new Error('No questions available at this time');
        }

        setQuestions(allQuestions);
        const initialResponses = {};
        allQuestions.forEach((_, index) => {
          initialResponses[index] = '';
        });
        setResponses(initialResponses);
      } catch (err) {
        console.error('Detailed Error:', err);
        setError(err.message || 'Failed to load questions. Please try again.');
        // Don't navigate away, just show error message
      } finally {
        setLoading(false);
      }
    };

    if (!promptId || !projectId) {
      setError('Missing required parameters');
      return;
    }

    fetchQuestions();
  }, [promptId, projectId, navigate]);

  const handleResponseChange = (index, value) => {
    setResponses(prev => ({
      ...prev,
      [index]: value
    }));
  };

// Clarifications.js - Updated handleNext function
const handleNext = async () => {
  try {
    const allEmpty = Object.values(responses).every(response => !response.trim());

    if (allEmpty) {
      const proceed = window.confirm(
        'You have not provided any responses. Would you like to proceed anyway?'
      );
      if (!proceed) return;
    }

    // Prepare the answered questions
    const answeredQuestions = questions.map((item, index) => ({
      question: item.question,
      answer: responses[index]
    })).filter(qa => qa.answer.trim() !== '');

    // Navigate to status page immediately
    navigate('/prompt-status-2', {
      state: {
        projectId,
        promptId,
        isProcessing: true
      }
    });

    // Make API call after navigation
    try {
      await submitClarificationResponses(projectId, promptId, answeredQuestions);
    } catch (error) {
      // If this is a subscription error (detected before making the API call)
      if (error.isSubscriptionError) {
        navigate('/prompt-status-2', {
          state: {
            projectId,
            promptId,
            error: error.message,
            isSubscriptionError: true,
            // Pass the subscription status if available in the error
            subscriptionStatus: error.subscriptionStatus || 'expired'
          },
          replace: true
        });
      } else {
        navigate('/prompt-status-2', {
          state: {
            projectId,
            error: 'Failed to submit responses. Please try again.'
          },
          replace: true
        });
      }
    }
  } catch (error) {
    console.error('Error in handleNext function:', error);
    navigate('/prompt-status-2', {
      state: {
        projectId,
        error: 'An unexpected error occurred. Please try again.'
      },
      replace: true
    });
  }
};

  if (loading) {
    return (
      <div className='min-h-screen flex flex-col'>
        <Navbar4
          title='Create View'
          backPath='/create-view'
          projectId={projectId}
        />
        <div className='flex-grow flex items-center justify-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900' />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='min-h-screen flex flex-col'>
        <Navbar4
          title='Create View'
          backPath='/create-view'
          projectId={projectId}
        />
        <div className='flex-grow flex items-center justify-center'>
          <div className='text-red-500'>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen flex flex-col'>
      <Navbar4
        title='Create View'
        backPath='/create-view'
        projectId={projectId}
        step={2}
      />

      <div className='flex-grow p-6'>
        <div className='max-w-7xl mx-auto'>
        
        <div className='flex items-center justify-between mb-8'>
          {/* Describe Section */}
          <div className='flex items-center gap-4 z-10 bg-white pr-4'>
            <div className='w-8 h-8 flex items-center justify-center bg-gray-300 text-white rounded-full'>
              1
            </div>
            <h1 className='text-2xl font-bold text-gray-300'>Describe</h1>
          </div>

          {/* Line between Describe and Clarify */}
          <div className='flex-1 h-0.5 bg-gray-300 absolute left-1/4 right-1/4 transform -translate-y-1/2 z-0'></div>

          {/* Clarify Section */}
          <div className='flex items-center gap-4 z-10 bg-white px-4'>
            <div className='w-8 h-8 flex items-center justify-center bg-black text-white rounded-full'>
              2
            </div>
            <h1 className='text-2xl font-bold text-gray-800'>Clarify</h1>
          </div>
          </div>

          <p className='text-lg text-gray-600 mb-8 text-center'>
            Please answer all applicable clarification questions below.
          </p>

          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
            {questions.map((item, index) => (
              <QuestionCard
                key={index}
                category={item.category}
                question={item.question}
                value={responses[index] || ''}
                onChange={(value) => handleResponseChange(index, value)}
              />
            ))}
          </div>

          <div className='mt-8 flex justify-center'>
            <button
              onClick={handleNext}
              className='flex items-center gap-2 px-6 py-3 bg-black text-white rounded-md hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed'
            ><Sparkles className='w-5 h-5' />
              Create View
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Clarifications;